import { doc, getDoc, setDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import {
  saveChapterContentToStorage,
  saveRecoveryCheckpointToStorage,
  loadRecoveryCheckpointFromStorage,
  deleteCheckpointFromStorage,
} from '../../../services/storageChapter.service';
import { patchDoc } from '../../utils/firestoreWrite';
import type { Chapter, FullProjectState, ProjectMetadata, StoryBible } from '../../types';
import { AppStatus } from '../../types';
import type {
  OriginalEngineCompatRepository,
  OriginalEngineProjectRepository,
  OriginalEngineSequelRepository,
} from '../types';
import { stripExperimentalLedgerFields } from '../adapters/InMemoryCompatRepository';
import {
  createCompatRepositoryFromDeps,
  createCompatSequelRepositoryFromDeps,
  compatCheckpointStoragePath,
} from '../compat/compatRepository.factory';
import { normalizeProjectFromFirestore } from '../../utils/projectLoader';
import type { OriginalCompatCheckpointBody } from '../compat/compatTypes';

function firestoreDeps(uid: string) {
  return {
    uid,
    async loadStateDoc(projectId: string) {
      const snap = await getDoc(doc(db, 'users', uid, 'projects', projectId, 'state', 'current'));
      if (!snap.exists()) throw new Error(`Project state not found: ${projectId}`);
      return snap.data() as Record<string, unknown>;
    },
    async loadProjectMeta(projectId: string) {
      const snap = await getDoc(doc(db, 'users', uid, 'projects', projectId));
      return snap.exists() ? (snap.data() as Record<string, unknown>) : {};
    },
    async patchStateDoc(projectId: string, data: Record<string, unknown>) {
      await patchDoc(doc(db, 'users', uid, 'projects', projectId, 'state', 'current'), data);
    },
    async patchProjectMeta(projectId: string, data: Record<string, unknown>) {
      await patchDoc(doc(db, 'users', uid, 'projects', projectId), data);
    },
    async setProjectMeta(projectId: string, data: Record<string, unknown>) {
      await setDoc(doc(db, 'users', uid, 'projects', projectId), data);
    },
    async setStateDoc(projectId: string, data: Record<string, unknown>) {
      await setDoc(doc(db, 'users', uid, 'projects', projectId, 'state', 'current'), data);
    },
    async patchCheckpointMeta(projectId: string, docId: string, data: Record<string, unknown>) {
      await patchDoc(doc(db, 'users', uid, 'projects', projectId, 'checkpoints', docId), data);
    },
    async getCheckpointMeta(projectId: string, docId: string) {
      const snap = await getDoc(doc(db, 'users', uid, 'projects', projectId, 'checkpoints', docId));
      return snap.exists() ? (snap.data() as Record<string, unknown>) : null;
    },
    async getCommitRecord(projectId: string, commitKey: string) {
      const snap = await getDoc(doc(db, 'users', uid, 'projects', projectId, 'commits', commitKey));
      return snap.exists() ? (snap.data() as Record<string, unknown>) : null;
    },
    async setCommitRecord(projectId: string, commitKey: string, data: Record<string, unknown>) {
      await setDoc(doc(db, 'users', uid, 'projects', projectId, 'commits', commitKey), data);
    },
    async listProjects() {
      const snap = await getDocs(collection(db, 'users', uid, 'projects'));
      return snap.docs.map((d) => ({ id: d.id, data: d.data() as Record<string, unknown> }));
    },
    storage: {
      async saveCheckpointBody(_uid: string, storagePath: string, body: OriginalCompatCheckpointBody) {
        await saveRecoveryCheckpointToStorage(storagePath, body);
      },
      async loadCheckpointBody(storagePath: string) {
        return loadRecoveryCheckpointFromStorage<OriginalCompatCheckpointBody>(storagePath);
      },
      async deleteCheckpointBody(storagePath: string) {
        await deleteCheckpointFromStorage(storagePath);
      },
      async saveChapterContent(
        u: string,
        projectId: string,
        chapterNumber: number,
        content: string,
        title?: string,
        summary?: string,
      ) {
        await saveChapterContentToStorage(u, projectId, chapterNumber, content, title, summary);
      },
    },
    stripLedger<T extends Record<string, unknown>>(ledger: T) {
      return stripExperimentalLedgerFields(ledger);
    },
  };
}

export function createFirestoreOriginalEngineRepository(uid: string): OriginalEngineCompatRepository {
  return createCompatRepositoryFromDeps(firestoreDeps(uid));
}

export function createFirestoreOriginalSequelRepository(uid: string): OriginalEngineSequelRepository {
  return createCompatSequelRepositoryFromDeps(firestoreDeps(uid));
}

/** @deprecated use createFirestoreOriginalEngineRepository */
export function createFirestoreOriginalEngineRepositoryLegacy(uid: string): OriginalEngineProjectRepository {
  return createFirestoreOriginalEngineRepository(uid);
}

export async function loadOriginalEngineProjectList(uid: string): Promise<ProjectMetadata[]> {
  const projectsRef = collection(db, 'users', uid, 'projects');
  const snap = await getDocs(projectsRef);
  return snap.docs.map((d) => normalizeProjectFromFirestore(d.id, d.data(), uid));
}

export const ORIGINAL_STAGE_LABELS = {
  SCRIBE: 'Writing chapter',
  INSPECTOR: 'Checking continuity',
  EDITOR: 'Polishing chapter',
  PUBLICATION_EDITOR: 'Finalizing manuscript polish',
  ARCHIVIST: 'Updating Story Memory',
  ARCHITECT: 'Revising chapter plan',
  DIRECTOR: 'Director guidance',
} as const;

export type OriginalStageKey = keyof typeof ORIGINAL_STAGE_LABELS;

export function mapOriginalAgentToProgressLabel(agent: OriginalStageKey): string {
  return ORIGINAL_STAGE_LABELS[agent];
}

export { compatCheckpointStoragePath };
